import React from 'react';
import { isOrganizationEventMessage, getEventData } from './organization-events/eventParser';
import EventCards from './organization-events/EventCards';
import { isCalendarEventMessage, getCalendarEventData } from './calendar-events/calendarEventParser';
import CalendarEventCards from './calendar-events/CalendarEventCards';
import { isBookingListMessage, getBookingListData } from './booking-list/bookingListParser';
import BookingListCards from './booking-list/BookingListCards';
import { isCancelBookingMessage, getCancelBookingData } from './cancel-booking/cancelBookingParser';
import CancelBookingCards from './cancel-booking/CancelBookingCards';
import { isUpdateBookingMessage, getUpdateBookingData } from './update-booking/updateBookingParser';
import UpdateBookingCards from './update-booking/UpdateBookingCards';
import UpdateConfirmationCard from './update-booking/UpdateConfirmationCard';
import {
  isCommunityAnnouncementMessage,
  getCommunityAnnouncementData,
} from './community-announcement/communityAnnouncementParser';
import CommunityAnnouncementCard from './community-announcement/CommunityAnnouncementCard';
import {
  isOrganizationEventProactiveMessage,
  getProactiveEventData,
} from './organization-event-proactive/proactiveEventParser';
import ProactiveEventCard from './organization-event-proactive/ProactiveEventCard';

/**
 * Render the appropriate card component for an assistant message.
 * Returns null if the message is not a card type, is still typing,
 * or is a user message.
 *
 * Props:
 *   - message: the chat message object
 *   - typingMessageId: currently-typing message ID (cards hidden during typing)
 *   - onImageLoad: callback when a card image loads (for scroll adjustment)
 *   - onCancelSelect: callback(resourceName) when user clicks a cancel-booking card
 *   - onUpdateSelect: callback(resourceName) when user clicks an update-booking card
 *   - onBookRoom: callback(event) when user clicks the "Book Meeting Room" button
 *     on a calendar event card
 *   - onImageClick: callback(urls, startIndex) — fired when a CA card tile is
 *     clicked. Parent typically opens a lightbox cycling through the urls.
 *   - collapseSignal: changes when a new user message is sent (used by cards with
 *     expandable details to auto-collapse when the user types something new)
 */
const MessageCardRenderer = ({ message, typingMessageId, onImageLoad, onCancelSelect, onUpdateSelect, onBookRoom, onImageClick, collapseSignal }) => {
  if (message.role !== 'assistant' || typingMessageId === message.id) return null;

  // Organization event proactive (single suggested event)
  const proactiveData = isOrganizationEventProactiveMessage(message)
    ? getProactiveEventData(message)
    : null;
  if (proactiveData) {
    return (
      <ProactiveEventCard
        data={proactiveData}
        onImageLoad={onImageLoad}
        collapseSignal={collapseSignal}
      />
    );
  }

  // Organization events
  const events = isOrganizationEventMessage(message) ? getEventData(message) : null;
  if (events?.length > 0) {
    return <EventCards events={events} onImageLoad={onImageLoad} collapseSignal={collapseSignal} />;
  }

  // Calendar events
  const calData = isCalendarEventMessage(message) ? getCalendarEventData(message) : null;
  if (calData?.events?.length > 0) {
    return (
      <>
        <CalendarEventCards data={calData} onLoad={onImageLoad} onBookRoom={onBookRoom} />
        {calData.freeTimeSummary && (
          <p className="calendar-events-free-time">{calData.freeTimeSummary}</p>
        )}
        {calData.limitNotice && (
          <p className="calendar-events-free-time">{calData.limitNotice}</p>
        )}
        {calData.resumePrompt && (
          <p className="calendar-events-resume-prompt">{calData.resumePrompt}</p>
        )}
      </>
    );
  }

  // Booking list
  const bookingListData = isBookingListMessage(message) ? getBookingListData(message) : null;
  if (bookingListData) {
    return (
      <>
        <BookingListCards
          bookings={bookingListData.bookings}
          onImageLoad={onImageLoad}
        />
        {bookingListData.resumePrompt && (
          <p className="booking-list-resume-prompt">{bookingListData.resumePrompt}</p>
        )}
      </>
    );
  }

  // Cancel booking
  const cancelData = isCancelBookingMessage(message) ? getCancelBookingData(message) : null;
  if (cancelData) {
    const isConfirmation = message.raw_output?.conversation_state === 'cancel_awaiting_confirmation';
    return (
      <>
        {isConfirmation ? (
          <BookingListCards bookings={cancelData.bookings} onImageLoad={onImageLoad} />
        ) : (
          <CancelBookingCards
            bookings={cancelData.bookings}
            onSelect={onCancelSelect}
            onImageLoad={onImageLoad}
          />
        )}
        {cancelData.selectionPrompt && (
          <p className="booking-select-prompt">{cancelData.selectionPrompt}</p>
        )}
      </>
    );
  }

  // Update booking
  const updateData = isUpdateBookingMessage(message) ? getUpdateBookingData(message) : null;
  if (updateData) {
    const isConfirmation = updateData.conversationState === 'update_awaiting_confirmation';
    if (isConfirmation) {
      return (
        <UpdateConfirmationCard
          booking={updateData.bookings[0]}
          updateHeader={updateData.updateHeader}
          diffBlock={updateData.diffBlock}
          onImageLoad={onImageLoad}
        />
      );
    }
    if (updateData.conversationState === 'update_list_shown') {
      return (
        <>
          <UpdateBookingCards
            bookings={updateData.bookings}
            onSelect={onUpdateSelect}
            onImageLoad={onImageLoad}
          />
          {updateData.selectionPrompt && (
            <p className="booking-select-prompt">{updateData.selectionPrompt}</p>
          )}
        </>
      );
    }
    if (updateData.conversationState === 'update_collecting_fields') {
      return (
        <>
          <BookingListCards bookings={updateData.bookings} onImageLoad={onImageLoad} />
          {updateData.selectionPrompt && (
            <p className="booking-select-prompt">{updateData.selectionPrompt}</p>
          )}
        </>
      );
    }
    // Other update states (room selection, alt time) — text only
    return null;
  }

  // Community announcement (author preview / director approval).
  // Returns null when raw_output has no ca_card — that case is plain status
  // text (e.g. "Got it — your announcement is queued…") and renders as a
  // normal assistant message.
  const caData = isCommunityAnnouncementMessage(message)
    ? getCommunityAnnouncementData(message)
    : null;
  if (caData) {
    return (
      <CommunityAnnouncementCard
        data={caData}
        onImageLoad={onImageLoad}
        onImageClick={onImageClick}
      />
    );
  }

  return null;
};

export default MessageCardRenderer;
